import * as React from 'react'
import { connect } from 'react-redux'
import { useState } from 'react'
import { Position } from '@blueprintjs/core'
import config from '../../../../../config'
import { ShopifyListing } from '../../../../../types/api'
import {
  CollectionsInput,
  CompareAtPriceInput,
  DurationInput,
  LocationsInput,
  PriceInput,
  ProductTypeInput,
  QuantityInput,
  ScheduleTimePicker,
  TagsInput,
  TemplateSuffixInput,
  VendorInput,
  UPCInput
} from './Inputs'
import { AppState } from '../../../../../store'
import { CurrentState } from '../../../../../current/types'
import { PRICE_OVERRIDES_DISABLED_PRICE_INPUT_TITLE_LABEL } from '../utils'
import { PriceRecommendation } from '../PricePrediction/PriceRecommendation'

export interface ShopifyListingForm extends Partial<ShopifyListing> {
  vendor: string
  rawCollections: string
  locationChangeUnsafeReason?: string
  isConcurrent?: boolean
  useCAD?: boolean
  useProductCategoryAsTags?: boolean
  scheduleTime?: string
  id?: number
  externalMarketId?: string
}
interface Props {
  form: ShopifyListingForm
  onFormChange: any
  errors: any
  productTypeFallback?: string
  skipErrorPrefix?: boolean
  hasVariants: boolean
  syncProductTags: boolean
  current: CurrentState
  sectionOpen?: boolean
  disablePriceInputs?: boolean
  priceRecommendation?: any
  productUnitTracking: boolean
}

// The majority of this component is the same as ShopifyFormV2.tsx, except for some layout changes.
// NB: If you make a logic change here, you'll need to make it there as well.
const ShopifyForm: React.FC<Props> = ({
  form,
  onFormChange,
  productTypeFallback,
  errors,
  hasVariants,
  syncProductTags,
  skipErrorPrefix,
  current,
  sectionOpen,
  disablePriceInputs,
  priceRecommendation,
  productUnitTracking
}) => {
  // Helps to show "Required" label. See comment in client/routes/products_v5/components/JoinedForm/ShopgoodwillCard/ShopgoodwillForm.tsx
  const [initialForm] = useState(form)

  // TODO(product_inventory_unit): Improve disabled state when implementing product inventory tracking for all marketplaces and/or lister books
  const quantityDisabled = productUnitTracking || hasVariants
  let quantityDisabledHelperText = ''
  if (productUnitTracking === true) {
    quantityDisabledHelperText = 'Products with inventory units automatically manage quantity'
  }

  return (
    <div className="shopify-form">
      {hasVariants === false && (
        <>
          <PriceInput
            error={skipErrorPrefix ? errors.price : errors.shopifyListingPrice}
            showRequiredLabel={
              current.organization.productFormRequiredFields.includes('shopifyListingPrice') && !initialForm.price
            }
            value={form.price}
            onChange={onFormChange}
            useCAD={form.useCAD}
            errorTooltipOffset={sectionOpen && 45}
            disabled={disablePriceInputs}
            title={disablePriceInputs && PRICE_OVERRIDES_DISABLED_PRICE_INPUT_TITLE_LABEL}
          />
          <CompareAtPriceInput
            error={skipErrorPrefix ? errors.compareAtPrice : errors.shopifyListingCompareAtPrice}
            showRequiredLabel={
              current.organization.productFormRequiredFields.includes('shopifyListingCompareAtPrice') &&
              !initialForm.compareAtPrice
            }
            value={form.compareAtPrice}
            onChange={onFormChange}
            useCAD={form.useCAD}
            disabled={disablePriceInputs}
            title={disablePriceInputs && PRICE_OVERRIDES_DISABLED_PRICE_INPUT_TITLE_LABEL}
          />
          {/* TODO(product_inventory_unit): Improve disabled state when implementing product inventory tracking for all marketplaces and/or lister books */}
          <QuantityInput
            error={skipErrorPrefix ? errors.quantity : errors.shopifyListingQuantity}
            onChange={onFormChange}
            value={form.quantity}
            disabled={quantityDisabled}
            helperText={quantityDisabled ? quantityDisabledHelperText : ''}
            errorTooltipPosition={Position.RIGHT}
            errorTooltipOffset={sectionOpen && 198}
          />
          <div />
          {/* grid spacer */}
        </>
      )}

      <PriceRecommendation listingType="BuyItNow" onChange={onFormChange} recommendations={priceRecommendation} />
      <div className="stretch-two">
        {syncProductTags !== true && (
          <TagsInput
            error={skipErrorPrefix ? errors.tags : errors.shopifyListingTags}
            values={form.tags}
            requiredValues={form.requiredTags}
            onChange={onFormChange}
            useProductCategoryAsTags={form.useProductCategoryAsTags}
          />
        )}
      </div>
      <div />
      {/* grid spacer */}
      <LocationsInput
        error={skipErrorPrefix ? errors.location : errors.shopifyListingLocation}
        showRequiredLabel={
          current.organization.productFormRequiredFields.includes('shopifyListingLocation') && !initialForm.locationId
        }
        value={form.locationId}
        marketAccountId={form.marketAccountId}
        disabledReason={form.locationChangeUnsafeReason}
        onChange={onFormChange}
        errorTooltipOffset={sectionOpen && 45}
      />
      <DurationInput
        error={skipErrorPrefix ? errors.duration : errors.shopifyListingDuration}
        value={form.duration}
        onChange={onFormChange}
        disabled={form.isConcurrent}
      />
      {config.SHOPIFY_VARIANTS() ? (
        <ScheduleTimePicker value={form.scheduleTime} onChange={onFormChange} />
      ) : (
        // empty grid space
        <div />
      )}
      <ProductTypeInput
        error={skipErrorPrefix ? errors.productType : errors.shopifyListingProductType}
        productValue={productTypeFallback}
        value={form.productType}
        onChange={onFormChange}
      />
      <UPCInput
        error={skipErrorPrefix ? errors.barcode : errors.shopifyListingBarcode}
        value={form.barcode}
        onChange={onFormChange}
      />
      {config.SHOPIFY_VENDOR_DROPDOWN() ? (
        <VendorInput
          error={errors.vendor}
          value={form.vendor}
          marketAccountId={form.marketAccountId}
          onChange={onFormChange}
        />
      ) : (
        <div /> // grid spacer
      )}
      <div className="stretch-two">
        <CollectionsInput
          error={skipErrorPrefix ? errors.collections : errors.shopifyListingCollections}
          value={form.rawCollections}
          marketAccountId={form.marketAccountId}
          onChange={onFormChange}
        />
      </div>
      <div />
      {/* grid spacer */}
      <TemplateSuffixInput
        error={skipErrorPrefix ? errors.templateSuffix : errors.shopifyListingTemplateSuffix}
        value={form.templateSuffix}
        onChange={onFormChange}
      />
      <div />
      {/* grid spacer */}
    </div>
  )
}

export default connect((state: AppState) => ({ current: state.current }))(React.memo(ShopifyForm))
